import React from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  SimpleGrid,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const RISK_COLORS = {
  conservative: '#2563EB',
  balanced: '#F59E0B',
  growth: '#DC2626',
};

const CLUSTER_COLORS = ['#0F766E', '#2563EB', '#D97706', '#7C3AED', '#DB2777'];

const formatMonthLabel = monthKey => {
  if (!monthKey || typeof monthKey !== 'string') {
    return 'Unknown';
  }

  const [year, month] = monthKey.split('-');
  const date = new Date(`${year}-${month || '01'}-01T00:00:00Z`);

  if (Number.isNaN(date.getTime())) {
    return monthKey;
  }

  return new Intl.DateTimeFormat('en', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

const getLeadingCluster = trendRow => {
  if (!trendRow) {
    return null;
  }

  return Object.entries(trendRow)
    .filter(([key]) => key !== 'month')
    .sort((left, right) => right[1] - left[1])[0]?.[0] || null;
};

function UserMLResp({ summaryData = {}, onRefresh }) {
  const summary = summaryData?.summary || {};
  const conversionComparison = summaryData?.conversionComparison || {};
  const riskBandTrend = Array.isArray(summaryData?.riskBandTrend)
    ? summaryData.riskBandTrend
    : [];
  const dominantClusterTrend = Array.isArray(summaryData?.dominantClusterTrend)
    ? summaryData.dominantClusterTrend
    : [];
  const clusterDistribution = Array.isArray(summaryData?.clusterDistribution)
    ? summaryData.clusterDistribution
    : [];
  const topBiases = Array.isArray(summaryData?.topBiases)
    ? summaryData.topBiases
    : [];
  const updatedAt = summaryData?.updatedAt;

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  const monthlyRows = riskBandTrend.map(riskRow => {
    const matchingClusterRow = dominantClusterTrend.find(
      clusterRow => clusterRow.month === riskRow.month,
    );

    return {
      month: riskRow.month,
      conservative: riskRow.conservative || 0,
      balanced: riskRow.balanced || 0,
      growth: riskRow.growth || 0,
      leadingCluster: getLeadingCluster(matchingClusterRow) || 'Unknown',
    };
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const riskTrendData = {
    labels: riskBandTrend.map(item => formatMonthLabel(item.month)),
    datasets: [
      {
        label: 'Conservative',
        data: riskBandTrend.map(item => item.conservative || 0),
        borderColor: RISK_COLORS.conservative,
        backgroundColor: 'rgba(37, 99, 235, 0.16)',
        tension: 0.3,
      },
      {
        label: 'Balanced',
        data: riskBandTrend.map(item => item.balanced || 0),
        borderColor: RISK_COLORS.balanced,
        backgroundColor: 'rgba(245, 158, 11, 0.16)',
        tension: 0.3,
      },
      {
        label: 'Growth',
        data: riskBandTrend.map(item => item.growth || 0),
        borderColor: RISK_COLORS.growth,
        backgroundColor: 'rgba(220, 38, 38, 0.16)',
        tension: 0.3,
      },
    ],
  };

  const clusterNames = Array.from(
    new Set(
      dominantClusterTrend.flatMap(item =>
        Object.keys(item).filter(key => key !== 'month' && item[key] > 0),
      ),
    ),
  );

  const clusterTrendData = {
    labels: dominantClusterTrend.map(item => formatMonthLabel(item.month)),
    datasets: clusterNames.map((clusterName, index) => ({
      label: clusterName,
      data: dominantClusterTrend.map(item => item[clusterName] || 0),
      borderColor: CLUSTER_COLORS[index % CLUSTER_COLORS.length],
      backgroundColor: `${CLUSTER_COLORS[index % CLUSTER_COLORS.length]}22`,
      tension: 0.3,
    })),
  };

  const conversionData = {
    labels: ['Deep dive completed', 'Deep dive pending'],
    datasets: [
      {
        label: 'Users',
        data: [
          conversionComparison.completed?.users || 0,
          conversionComparison.pending?.users || 0,
        ],
        backgroundColor: 'rgba(148, 163, 184, 0.45)',
      },
      {
        label: 'Premium users',
        data: [
          conversionComparison.completed?.premiumUsers || 0,
          conversionComparison.pending?.premiumUsers || 0,
        ],
        backgroundColor: 'rgba(37, 99, 235, 0.75)',
      },
    ],
  };

  const clusterDistributionData = {
    labels: clusterDistribution.map(item => item.cluster),
    datasets: [
      {
        data: clusterDistribution.map(item => item.count),
        backgroundColor: clusterDistribution.map(
          (_, index) => CLUSTER_COLORS[index % CLUSTER_COLORS.length],
        ),
        borderWidth: 1,
      },
    ],
  };

  const topBiasData = {
    labels: topBiases.map(item => item.bias),
    datasets: [
      {
        label: 'Frequency',
        data: topBiases.map(item => item.count),
        backgroundColor: 'rgba(15, 118, 110, 0.72)',
        borderColor: 'rgba(15, 118, 110, 1)',
        borderWidth: 1,
      },
    ],
  };

  const leadingCluster = clusterDistribution[0]?.cluster || 'Unknown';
  const topBias = topBiases[0]?.bias || 'Unknown';
  const premiumLiftTone =
    Number(summary.premiumConversionLift || 0) > 0 ? 'green.600' : 'gray.700';

  return (
    <Box minH="100vh" bg={pageBg}>
      <Box maxW="7xl" mx="auto" px={6} py={6}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ base: 'flex-start', md: 'center' }}
          flexDirection={{ base: 'column', md: 'row' }}
          gap={4}
          mb={8}
        >
          <Box>
            <Heading size="xl" mb={2}>
              AI Governance & Conversion Analytics
            </Heading>
            <Text color="gray.600" maxW="3xl">
              Governance view of behavioral profiling quality, monthly risk-band
              evolution and the conversion lift generated by the behavioral deep
              dive.
            </Text>
          </Box>
          <Button colorScheme="blue" onClick={onRefresh}>
            Refresh
          </Button>
        </Box>

        <SimpleGrid columns={[1, 2, 3, 6]} spacing={4} mb={8}>
          <MetricCard
            label="Profiled Users"
            value={summary.totalProfiledUsers || 0}
            help="Users with saved ML profiling output"
            tone="blue"
          />
          <MetricCard
            label="Avg Risk Score"
            value={summary.averageRiskScore || 0}
            help="0-10 weighted average"
            tone="orange"
          />
          <MetricCard
            label="Deep Dive Completion"
            value={`${summary.behavioralDeepDiveCompletionRate || 0}%`}
            help={`${summary.behavioralDeepDiveCompleted || 0} users completed`}
            tone="green"
          />
          <MetricCard
            label="Premium if Completed"
            value={`${summary.premiumConversionCompletedRate || 0}%`}
            help="Conversion among completed users"
            tone="purple"
          />
          <MetricCard
            label="Premium if Pending"
            value={`${summary.premiumConversionPendingRate || 0}%`}
            help="Conversion among non-completed users"
            tone="gray"
          />
          <MetricCard
            label="Deep Dive Lift"
            value={`${summary.premiumConversionLift || 0} pts`}
            help="Completed minus pending"
            tone="teal"
          />
        </SimpleGrid>

        <SimpleGrid columns={[1, 1, 2]} spacing={6} mb={6}>
          <ChartCard title="Risk Band Trend Over Time" bg={cardBg}>
            {riskBandTrend.length ? (
              <Line data={riskTrendData} options={chartOptions} />
            ) : (
              <EmptyChartState label="No monthly risk trend available yet." />
            )}
          </ChartCard>
          <ChartCard title="Dominant Behavioral Clusters Over Time" bg={cardBg}>
            {dominantClusterTrend.length ? (
              <Line data={clusterTrendData} options={chartOptions} />
            ) : (
              <EmptyChartState label="No cluster trend available yet." />
            )}
          </ChartCard>
        </SimpleGrid>

        <SimpleGrid columns={[1, 1, 2]} spacing={6} mb={6}>
          <ChartCard title="Deep Dive vs Premium Conversion" bg={cardBg}>
            <Bar
              data={conversionData}
              options={{
                ...chartOptions,
                scales: {
                  y: {
                    beginAtZero: true,
                  },
                },
              }}
            />
          </ChartCard>
          <Card bg={cardBg} shadow="sm">
            <CardHeader pb={2}>
              <Heading size="md">Strategic Readout</Heading>
            </CardHeader>
            <CardBody>
              <Text fontSize="sm" color="gray.600" mb={3}>
                This panel translates the analytics into product and conversion
                signals for the admin team.
              </Text>
              <InsightRow
                label="Deep dive ROI"
                value={`${summary.premiumConversionLift || 0} pts premium lift`}
                tone={premiumLiftTone}
              />
              <InsightRow
                label="Leading cluster"
                value={leadingCluster}
                tone="blue.600"
              />
              <InsightRow
                label="Most recurring bias"
                value={topBias}
                tone="teal.600"
              />
              <InsightRow
                label="Latest update"
                value={updatedAt ? new Date(updatedAt).toLocaleString() : 'n/a'}
                tone="gray.700"
              />
              <Box
                mt={5}
                p={4}
                borderRadius="xl"
                bg="blue.50"
                border="1px solid"
                borderColor="blue.100"
              >
                <Text fontSize="sm" fontWeight="600" mb={1}>
                  Governance note
                </Text>
                <Text fontSize="sm" color="gray.700">
                  If the lift stays positive across months, the behavioral deep
                  dive is acting as a measurable Premium conversion driver and
                  should stay central in the Free to Premium journey.
                </Text>
              </Box>
            </CardBody>
          </Card>
        </SimpleGrid>

        <SimpleGrid columns={[1, 1, 2]} spacing={6} mb={6}>
          <ChartCard title="Current Cluster Distribution" bg={cardBg}>
            {clusterDistribution.length ? (
              <Doughnut data={clusterDistributionData} options={chartOptions} />
            ) : (
              <EmptyChartState label="No cluster distribution available yet." />
            )}
          </ChartCard>
          <ChartCard title="Top Recurring Biases" bg={cardBg}>
            {topBiases.length ? (
              <Bar
                data={topBiasData}
                options={{
                  ...chartOptions,
                  indexAxis: 'y',
                  plugins: {
                    ...chartOptions.plugins,
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    x: {
                      beginAtZero: true,
                    },
                  },
                }}
              />
            ) : (
              <EmptyChartState label="No bias recurrence data available yet." />
            )}
          </ChartCard>
        </SimpleGrid>

        <Card bg={cardBg} shadow="sm">
          <CardHeader pb={2}>
            <Heading size="md">Monthly Governance Pulse</Heading>
            <Text color="gray.600" mt={2}>
              Monthly distribution snapshot for risk bands and dominant
              behavioral driver.
            </Text>
          </CardHeader>
          <CardBody overflowX="auto">
            {monthlyRows.length ? (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Month</Th>
                    <Th isNumeric>Conservative</Th>
                    <Th isNumeric>Balanced</Th>
                    <Th isNumeric>Growth</Th>
                    <Th>Leading Cluster</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {monthlyRows.map(row => (
                    <Tr key={row.month}>
                      <Td>{formatMonthLabel(row.month)}</Td>
                      <Td isNumeric>{row.conservative}</Td>
                      <Td isNumeric>{row.balanced}</Td>
                      <Td isNumeric>{row.growth}</Td>
                      <Td>
                        <Badge colorScheme="blue" borderRadius="full" px={3}>
                          {row.leadingCluster}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <Text color="gray.600">No governance trend rows available yet.</Text>
            )}
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
}

function MetricCard({ label, value, help, tone = 'gray' }) {
  const tones = {
    blue: 'blue.500',
    orange: 'orange.500',
    green: 'green.500',
    purple: 'purple.500',
    gray: 'gray.700',
    teal: 'teal.500',
  };

  return (
    <Card shadow="sm">
      <CardBody>
        <Stat>
          <StatLabel>{label}</StatLabel>
          <StatNumber color={tones[tone] || tones.gray}>{value}</StatNumber>
          <StatHelpText mb={0}>{help}</StatHelpText>
        </Stat>
      </CardBody>
    </Card>
  );
}

function ChartCard({ title, children, bg }) {
  return (
    <Card bg={bg} shadow="sm">
      <CardHeader pb={2}>
        <Heading size="md">{title}</Heading>
      </CardHeader>
      <CardBody h="340px">{children}</CardBody>
    </Card>
  );
}

function InsightRow({ label, value, tone }) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      py={3}
      borderBottom="1px solid"
      borderColor="gray.100"
    >
      <Text color="gray.600">{label}</Text>
      <Text fontWeight="700" color={tone}>
        {value}
      </Text>
    </Box>
  );
}

function EmptyChartState({ label }) {
  return (
    <Box
      h="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      px={6}
    >
      <Text color="gray.500">{label}</Text>
    </Box>
  );
}

export default UserMLResp;
